import {
  Card,
  CircularProgress,
  Grid,
  Icon,
  MenuItem,
  Select,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import FormField from "components/FormField/FormField";
import UploadField from "components/FormField/FileUploadField";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { FieldArray, Form, Formik } from "formik";
import { useState } from "react";
import * as Yup from "yup";
import { useGetAllContent, useUpdateContents, useUploadSubContentImage } from "../content.hook";
import { Content, ETypeContent, SubContent } from "../content.type";
import { findAllContents } from "../content.service";

const MAX_SUB_SECTIONS = 3;

const APP_ROUTES = [
  { label: "Accueil", value: "index" },
  { label: "Programmation", value: "Programming" },
  { label: "Cartes", value: "Cartes" },
  { label: "Foire aux questions", value: "Faq" },
  { label: "Bénévolat", value: "Volunteer" },
  { label: "Notifications", value: "screens/Notifications" },
  { label: "Billetterie", value: "screens/BuyTickets" },
  { label: "Éco H2O", value: "screens/EcoH2o" },
  { label: "Hébergement", value: "screens/Hebergement" },
  { label: "Musique", value: "screens/programs/Musiq" },
  { label: "Sports", value: "screens/programs/Sports" },
  { label: "Activités hors murs", value: "screens/programs/Activities" },
  { label: "Famille", value: "screens/programs/FreeActivities" },
  { label: "Cartes détaillées", value: "screens/maps/Maps" },
];

type LinkMode = "internal" | "external";

type HousingSubContent = SubContent & {
  link_fr?: string;
  link_en?: string;
  image?: string;
  imageFile?: File;
};

type InitialValues = {
  titleFr: string;
  titleEn: string;
  bodyFr: string;
  bodyEn: string;
  labelButtonFr: string;
  labelButtonEn: string;
  linkButtonFr: string;
  linkButtonEn: string;
  subContent: HousingSubContent[];
};

type Props = {
  title: string;
  typeContent: ETypeContent;
};

function LinkField({
  label,
  name,
  value,
  onChange,
}: {
  label: string;
  name?: string;
  value: string;
  onChange: (val: string) => void;
}) {
  const isInternal = !!value && !value.startsWith("http");
  const [mode, setMode] = useState<LinkMode>(isInternal ? "internal" : "external");

  const handleModeChange = (_: any, newMode: LinkMode | null) => {
    if (!newMode) return;
    setMode(newMode);
    onChange("");
  };

  return (
    <MDBox>
      <MDTypography variant="caption" color="text" fontWeight="regular">
        {label}
      </MDTypography>
      <MDBox mt={0.5}>
        <ToggleButtonGroup value={mode} exclusive onChange={handleModeChange} size="small">
          <ToggleButton value="external">URL externe</ToggleButton>
          <ToggleButton value="internal">Page interne</ToggleButton>
        </ToggleButtonGroup>
      </MDBox>
      <MDBox mt={1}>
        {mode === "external" ? (
          <input
            type="text"
            placeholder="https://..."
            value={value}
            onChange={(e) => onChange(e.target.value)}
            style={{
              width: "100%",
              padding: "8px 12px",
              border: "1px solid #ccc",
              borderRadius: 6,
              fontSize: 14,
              outline: "none",
            }}
          />
        ) : (
          <Select
            value={value}
            onChange={(e) => onChange(e.target.value)}
            displayEmpty
            fullWidth
            size="small"
            sx={{ mt: 1 }}
          >
            <MenuItem value=""><em>Choisir une page</em></MenuItem>
            {APP_ROUTES.map((route) => (
              <MenuItem key={route.value} value={route.value}>
                {route.label}
              </MenuItem>
            ))}
          </Select>
        )}
      </MDBox>
    </MDBox>
  );
}

function ContentUpdateHousing({ title, typeContent }: Props): JSX.Element {
  const { data, isLoading } = useGetAllContent();
  const updateContents = useUpdateContents();
  const uploadSubContentImage = useUploadSubContentImage();

  const existing = data?.find((c) => c.id === typeContent);

  const initialValues: InitialValues = {
    titleFr: existing?.title_fr || "",
    titleEn: existing?.title_en || "",
    bodyFr: existing?.body_fr || "",
    bodyEn: existing?.body_en || "",
    labelButtonFr: existing?.subBody_fr || "",
    labelButtonEn: existing?.subBody_en || "",
    linkButtonFr: existing?.link1 || "",
    linkButtonEn: existing?.link2 || "",
    subContent: (existing?.subContent || []) as HousingSubContent[],
  };

  const validations = Yup.object().shape({
    titleFr: Yup.string().required("champ obligatoire"),
    titleEn: Yup.string().required("champ obligatoire"),
    bodyFr: Yup.string().required("champ obligatoire"),
    bodyEn: Yup.string().required("champ obligatoire"),
  });

  const handleSubmit = async (values: InitialValues, { resetForm }: any) => {
    try {
      // Collect files to upload before saving
      const filesToUpload: { index: number; file: File }[] = [];
      values.subContent.forEach((sub, index) => {
        if (sub.imageFile) {
          filesToUpload.push({ index, file: sub.imageFile });
        }
      });

      // Strip imageFile from subContent before sending to API
      const cleanSubContent = values.subContent.map(({ imageFile, ...rest }) => rest);

      // 1. Save content + sub-content first (creates new sub-content IDs)
      const contents: Content[] = [
        {
          id: typeContent,
          title_fr: values.titleFr,
          title_en: values.titleEn,
          body_fr: values.bodyFr,
          body_en: values.bodyEn,
          subBody_fr: values.labelButtonFr,
          subBody_en: values.labelButtonEn,
          link1: values.linkButtonFr,
          link2: values.linkButtonEn,
          subContent: cleanSubContent,
        },
      ];
      await updateContents.mutateAsync(contents);

      // 2. If there are images to upload, refetch to get the new sub-content IDs
      if (filesToUpload.length > 0) {
        const refreshed = await findAllContents();
        const updated = refreshed.find((c) => c.id === typeContent);
        const newSubContent = updated?.subContent || [];

        await Promise.all(
          filesToUpload.map(async ({ index, file }) => {
            const subId = newSubContent[index]?.id;
            if (subId) {
              await uploadSubContentImage.mutateAsync({ id: String(subId), file });
            }
          })
        );
      }

      resetForm({ values });
    } catch (error: any) {
      console.error("error:", error);
      alert(error);
    }
  };

  return (
    <>
      {isLoading ? (
        <MDBox display="flex" justifyContent="center" alignItems="center" height="60vh">
          <CircularProgress color="inherit" />
        </MDBox>
      ) : (
        <MDBox mt={4}>
          <Card style={{ overflowY: "auto" }}>
            <Formik
              initialValues={initialValues}
              validationSchema={validations}
              onSubmit={handleSubmit}
            >
              {({ values, errors, touched, dirty, isSubmitting, setFieldValue }) => (
                <Form autoComplete="off">
                  <MDBox my={3} m={4}>
                    {/* Titre section */}
                    <MDBox mb={4}>
                      <Grid container spacing={3} alignItems="center">
                        <Grid item xs={12} lg={6}>
                          <MDTypography variant="h4" fontWeight="medium">
                            {title}
                          </MDTypography>
                        </Grid>
                      </Grid>
                    </MDBox>

                    {/* Titre FR/EN */}
                    <MDBox mt={3}>
                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                          <FormField
                            type="text"
                            label="Titre - FR"
                            name="titleFr"
                            value={values.titleFr}
                            error={errors.titleFr && touched.titleFr}
                            success={values.titleFr?.length > 0 && !errors.titleFr}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormField
                            type="text"
                            label="Titre - EN"
                            name="titleEn"
                            value={values.titleEn}
                            error={errors.titleEn && touched.titleEn}
                            success={values.titleEn?.length > 0 && !errors.titleEn}
                          />
                        </Grid>
                      </Grid>
                    </MDBox>

                    {/* Description FR/EN */}
                    <MDBox mt={3}>
                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                          <FormField
                            type="text"
                            label="Description - FR"
                            name="bodyFr"
                            value={values.bodyFr}
                            error={errors.bodyFr && touched.bodyFr}
                            success={values.bodyFr?.length > 0 && !errors.bodyFr}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormField
                            type="text"
                            label="Description - EN"
                            name="bodyEn"
                            value={values.bodyEn}
                            error={errors.bodyEn && touched.bodyEn}
                            success={values.bodyEn?.length > 0 && !errors.bodyEn}
                          />
                        </Grid>
                      </Grid>
                    </MDBox>

                    {/* Bouton */}
                    <MDBox mt={4}>
                      <MDTypography variant="h5" fontWeight="regular" mb={2}>
                        Bouton (laisser vide pour ne pas afficher)
                      </MDTypography>
                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                          <FormField
                            type="text"
                            label="Label bouton - FR"
                            name="labelButtonFr"
                            value={values.labelButtonFr}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormField
                            type="text"
                            label="Label bouton - EN"
                            name="labelButtonEn"
                            value={values.labelButtonEn}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <LinkField
                            label="Lien de redirection bouton - FR"
                            value={values.linkButtonFr}
                            onChange={(val) => setFieldValue("linkButtonFr", val)}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <LinkField
                            label="Lien de redirection bouton - EN"
                            value={values.linkButtonEn}
                            onChange={(val) => setFieldValue("linkButtonEn", val)}
                          />
                        </Grid>
                      </Grid>
                    </MDBox>

                    {/* Sous-sections */}
                    <MDBox mt={4}>
                      <MDTypography variant="h5" fontWeight="regular" mb={2}>
                        Sous-sections (max {MAX_SUB_SECTIONS})
                      </MDTypography>
                      <FieldArray
                        name="subContent"
                        render={(arrayHelpers) => (
                          <>
                            {values.subContent.map((row, index) => (
                              <MDBox
                                key={index}
                                bgColor="#EEEEEE"
                                borderRadius={5}
                                mb={2}
                                pb={3}
                              >
                                <MDBox display="flex" justifyContent="flex-end">
                                  <MDButton
                                    variant="text"
                                    color="error"
                                    onClick={() => arrayHelpers.remove(index)}
                                  >
                                    <Icon>delete</Icon>
                                  </MDButton>
                                </MDBox>
                                <MDBox borderRadius={5} marginX={4} pb={2}>
                                  <MDBox mt={2}>
                                    <Grid container spacing={3}>
                                      <Grid item xs={12} sm={6}>
                                        <FormField
                                          type="text"
                                          label="Titre - FR"
                                          name={`subContent.${index}.title_fr`}
                                          defaultValue={row.title_fr}
                                        />
                                      </Grid>
                                      <Grid item xs={12} sm={6}>
                                        <FormField
                                          type="text"
                                          label="Titre - EN"
                                          name={`subContent.${index}.title_en`}
                                          defaultValue={row.title_en}
                                        />
                                      </Grid>
                                    </Grid>
                                  </MDBox>
                                  <MDBox mt={3}>
                                    <Grid container spacing={3}>
                                      <Grid item xs={12} sm={6}>
                                        <LinkField
                                          label="Lien de redirection - FR"
                                          value={row.link_fr || ""}
                                          onChange={(val) =>
                                            setFieldValue(`subContent.${index}.link_fr`, val)
                                          }
                                        />
                                      </Grid>
                                      <Grid item xs={12} sm={6}>
                                        <LinkField
                                          label="Lien de redirection - EN"
                                          value={row.link_en || ""}
                                          onChange={(val) =>
                                            setFieldValue(`subContent.${index}.link_en`, val)
                                          }
                                        />
                                      </Grid>
                                    </Grid>
                                  </MDBox>
                                  <MDBox mt={3}>
                                    <UploadField
                                      onUpload={(file: File) =>
                                        setFieldValue(`subContent.${index}.imageFile`, file)
                                      }
                                      newUploadLabel="Ajouter une image"
                                      updateUploadLabel="Modifier l'image"
                                      noFilelabel="Aucune image téléversée"
                                      seeFileLabel="Voir l'image actuelle"
                                      initialUrl={row.image}
                                    />
                                  </MDBox>
                                </MDBox>
                              </MDBox>
                            ))}
                            {values.subContent.length < MAX_SUB_SECTIONS && (
                              <MDBox display="flex" mt={2}>
                                <MDButton
                                  type="button"
                                  onClick={() => arrayHelpers.push({})}
                                  variant="outlined"
                                  color="info"
                                >
                                  + Ajouter une sous-section
                                </MDButton>
                              </MDBox>
                            )}
                          </>
                        )}
                      />
                    </MDBox>
                  </MDBox>

                  <MDBox p={2} display="flex" justifyContent="flex-end">
                    <MDButton
                      disabled={!dirty || isSubmitting}
                      type="submit"
                      variant="gradient"
                      color="info"
                    >
                      Valider
                      {isSubmitting && (
                        <CircularProgress
                          size={24}
                          sx={{
                            color: "inherit",
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            marginTop: "-12px",
                            marginLeft: "-12px",
                          }}
                        />
                      )}
                    </MDButton>
                  </MDBox>
                </Form>
              )}
            </Formik>
          </Card>
        </MDBox>
      )}
    </>
  );
}

export default ContentUpdateHousing;
